/**
 * CampusReactionsBar: barra de reacciones + toggle de comentarios para un post.
 */
import React from 'react';
import { ThumbsUp, MessageSquare } from 'lucide-react';

const REACTION_CONFIG = [
  { tipo: 'like', emoji: '👍', label: 'Me gusta' },
  { tipo: 'love', emoji: '❤️', label: 'Me encanta' },
  { tipo: 'interesa', emoji: '💡', label: 'Me interesa' },
  { tipo: 'sorprende', emoji: '😮', label: 'Me sorprende' },
  { tipo: 'triste', emoji: '😢', label: 'Me entristece' },
  { tipo: 'enoja', emoji: '😠', label: 'Me enoja' },
];

const totalReacciones = (post) =>
  Object.values(post.reacciones || {}).reduce((s, v) => s + v, 0);

export default function CampusReactionsBar({ post, ctx }) {
  const postReactions = post.reacciones || {};
  const myReaction = post.mi_reaccion;
  const tot = totalReacciones(post);
  const myMeta = REACTION_CONFIG.find((r) => r.tipo === myReaction);

  return (
    <div className="mt-3 pt-3 border-t">
      {tot > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`reactions-display-${post.id}`}>
          {REACTION_CONFIG.filter((r) => postReactions[r.tipo] > 0).map((r) => (
            <ReactionBadge key={r.tipo} r={r} post={post} ctx={ctx}
              myReaction={myReaction} count={postReactions[r.tipo]} />
          ))}
        </div>
      )}

      <div className="flex items-center gap-1">
        <ReactionTrigger post={post} ctx={ctx} myReaction={myReaction} myMeta={myMeta} />
        <span className="text-gray-200">|</span>
        {post.tipo !== 'encuesta' && (
          <button
            onClick={() => ctx.toggleComments(post.id)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs text-gray-500 hover:bg-gray-100 transition-colors"
            data-testid={`toggle-comments-${post.id}`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>
              {post.total_comentarios > 0
                ? `${post.total_comentarios} comentario${post.total_comentarios !== 1 ? 's' : ''}`
                : 'Comentar'}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

function ReactionBadge({ r, post, ctx, myReaction, count }) {
  const active = myReaction === r.tipo;
  return (
    <button
      onClick={() => ctx.toggleReaction(post.id, r.tipo)}
      disabled={ctx.reactingPost === post.id}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors ${
        active
          ? 'bg-violet-100 border-violet-300 text-violet-700'
          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
      }`}
      data-testid={`reaction-badge-${r.tipo}-${post.id}`}
    >
      <span>{r.emoji}</span>
      <span className="font-medium">{count}</span>
    </button>
  );
}

function ReactionTrigger({ post, ctx, myReaction, myMeta }) {
  const open = ctx.showReactionPicker === post.id;
  const timeoutRef = React.useRef(null);
  const wrapRef = React.useRef(null);

  const showSoon = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => ctx.setShowReactionPicker(post.id), 350);
  };
  const hideSoon = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      ctx.setShowReactionPicker((cur) => (cur === post.id ? null : cur));
    }, 250);
  };

  // Cleanup on unmount
  React.useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleTriggerClick = () => {
    // Click: toggle the picker (mobile / direct interaction)
    ctx.setShowReactionPicker(open ? null : post.id);
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={showSoon}
      onMouseLeave={hideSoon}
    >
      <button
        onClick={handleTriggerClick}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
          myReaction ? 'text-violet-600 hover:bg-violet-50' : 'text-gray-500 hover:bg-gray-100'
        }`}
        data-testid={`react-btn-${post.id}`}
      >
        {myMeta ? <span>{myMeta.emoji}</span> : <ThumbsUp className="w-3.5 h-3.5" />}
        <span>{myReaction ? 'Reaccion' : 'Reaccionar'}</span>
      </button>
      {open && (
        <div
          className="absolute bottom-full left-0 mb-1 bg-white rounded-lg shadow-lg border p-1.5 flex gap-0.5 z-20 animate-in fade-in zoom-in-95 duration-150"
          data-testid={`reaction-picker-${post.id}`}
          onMouseEnter={() => clearTimeout(timeoutRef.current)}
          onMouseLeave={hideSoon}
        >
          {REACTION_CONFIG.map((r) => (
            <ReactionOption key={r.tipo} r={r} post={post} ctx={ctx} myReaction={myReaction} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReactionOption({ r, post, ctx, myReaction }) {
  return (
    <button
      onClick={() => ctx.toggleReaction(post.id, r.tipo)}
      className={`w-8 h-8 flex items-center justify-center rounded-md text-lg hover:bg-gray-100 transition-transform hover:scale-125 ${
        myReaction === r.tipo ? 'bg-violet-100' : ''
      }`}
      title={r.label}
      data-testid={`reaction-option-${r.tipo}-${post.id}`}
    >
      {r.emoji}
    </button>
  );
}
